import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Trash2, ChevronRight, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/profiles');
        setProfiles(data);
      } catch (e) {
        toast.error('Failed to load profiles');
        setProfiles([]);
      }
    })();
  }, []);

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/profiles/${id}`);
      setProfiles(p => p.filter(x => x.id !== id));
      toast.success('Profile removed');
    } catch (e) {
      toast.error('Delete failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Dashboard</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Hello, {user?.email}</h1>
          <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Role: <span className="font-medium">{user?.role}</span></p>
        </div>
        <div className="flex gap-2">
          <Button onClick={()=>nav('/match')} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Run a biomarker match</Button>
          {user?.role === 'user' && <Button variant="outline" onClick={()=>nav('/pricing')}><Crown className="h-4 w-4 mr-1.5 text-[color:var(--accent-gold)]" /> Upgrade to Researcher</Button>}
        </div>
      </div>

      <section>
        <div className="flex items-end justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold tracking-tight">Saved biomarker profiles</h2>
          <Link to="/match" className="text-sm text-[color:var(--accent-teal)] underline underline-offset-4">+ Create profile via Match</Link>
        </div>
        {profiles === null ? (
          <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
        ) : profiles.length === 0 ? (
          <Card className="p-8 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-color)] bg-[color:var(--surface)]">
            <div className="text-sm font-medium">No saved profiles yet</div>
            <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Run a biomarker match to create and save your first profile.</p>
            <Button className="mt-4 bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]" onClick={()=>nav('/match')}>Start matching</Button>
          </Card>
        ) : (
          <div className="grid gap-3">
            {profiles.map(p => (
              <Card key={p.id} className="p-4 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="mt-1 text-xs text-[color:var(--muted-fg)]">Created {new Date(p.created_at).toLocaleString()}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {p.crp_mg_l != null && <span className="rounded-full border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-2 py-0.5">CRP {p.crp_mg_l} mg/L</span>}
                    {p.hba1c_percent != null && <span className="rounded-full border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-2 py-0.5">HbA1c {p.hba1c_percent}%</span>}
                    {p.il6_pg_ml != null && <span className="rounded-full border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-2 py-0.5">IL-6 {p.il6_pg_ml} pg/mL</span>}
                    {p.chronological_age != null && <span className="rounded-full border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-2 py-0.5">Age {p.chronological_age}y</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={()=>nav(`/match?profile=${p.id}`)}>Use in match <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" disabled={busy===p.id} onClick={()=>remove(p.id)} className="text-[color:var(--danger)]"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
